import { useCallback, useEffect, useMemo } from 'react';
import { Cart } from 'shared/types/cart';
import { Discount } from 'shared/types/cart/Discount';
import { Order } from 'shared/types/cart/Order';
import { Variant } from 'shared/types/product';
import useSWR, { mutate } from 'swr';
import useI18n from 'helpers/hooks/useI18n';
import mapCosts from 'helpers/utils/mapCosts';
import { sdk } from 'sdk';
import { revalidateOptions } from 'frontastic';
import { CartDetails, UseCartReturn } from './types';
import { ShippingAddress, ShippingMethod } from 'shared/types/cart/CustomShippingMethod';
import { AddCartItemPayload } from 'sdk/composable-commerce/types/payloads/CartPayloads';
import Cookies from 'js-cookie';
import { ITEMS_IN_CART } from 'helpers/constants/localStorage';

const useCart = (props: { shippingMethod?: ShippingMethod } = {}): UseCartReturn => {
  const { shippingMethod = undefined } = props;
  const extensions = sdk.composableCommerce;

  const { currency } = useI18n();

  const result = useSWR('/action/cart/getCart', extensions.cart.getCart, {
    ...revalidateOptions,
    revalidateIfStale: true,
  });

  const data = result.data?.isError ? {} : { data: result.data?.data as unknown as Cart };

  useEffect(() => {
    Cookies.set(ITEMS_IN_CART, JSON.stringify(!!data.data?.lineItems?.length));
  }, [data.data?.lineItems]);

  const estimateShipping = async (address: ShippingAddress) => {
    if (!address.zipcode) return;
    const shippingMethods = await extensions.cart.getCustomShippingMethods({
      address,
      items: data.data?.lineItems ?? [],
    });
    if (shippingMethods.isError)
      throw new Error(shippingMethods.error.message ?? shippingMethods.error?.response?.errors?.[0]?.message ?? '');
    return shippingMethods.data;
  };

  const totalItems = (data.data as Cart)?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !data?.data?.lineItems?.length;

  const isShippingAccurate = !!data?.data?.shippingInfo;

  const billingAddress = data?.data?.billingAddress;
  const shippingAddress = data?.data?.shippingAddress;

  const transaction = useMemo(
    () => mapCosts({ cart: data.data, currency, shippingMethod }),
    [data.data, currency, shippingMethod],
  );

  const addItem = useCallback(
    async (variant: Variant, quantity: number, finish?: string, sheen?: string, hasWirebrush?: boolean) => {
      try {
        const extensions = sdk.composableCommerce;
        const payload: AddCartItemPayload = {
          variant: {
            sku: variant.sku,
            count: quantity,
          },
        };

        if (finish) payload.variant.finish = finish;
        if (sheen) payload.variant.sheen = sheen;
        if (hasWirebrush) payload.variant.hasWirebrush = hasWirebrush;

        const res = await extensions.cart.addItem(payload);

        if (res.isError) {
          console.error('Error adding item to cart:', res.error);
          throw new Error(res.error?.message || 'Error adding item to cart');
        }

        mutate('/action/cart/getCart', res);

        return res.data as Cart;
      } catch (error) {
        console.error('Unexpected error adding item to cart:', error);
        throw error;
      }
    },
    [],
  );

  const orderCart = useCallback(async () => {
    const res = await sdk.callAction({ actionName: 'cart/checkout' });
    mutate('/action/cart/getCart');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const queryOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ orderIds: [orderId] });
    mutate('/action/cart/getCart');

    return (res.isError ? {} : res.data.items?.[0]) as Order;
  }, []);

  const getOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getOrder({ orderId });
    mutate('/action/cart/getOrder');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const orderHistory = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ sortAttributes: [{ createdAt: 'desc' }] });

    return res.isError ? ([] as Order[]) : (res.data.items as Order[]);
  }, []);

  const getProjectSettings = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.project.getSettings();

    return res.isError ? {} : res.data;
  }, []);

  const removeItem = useCallback(async (lineItemId: string, customLineItemId?: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: { id: lineItemId, customLineItemId },
    };

    const res = await extensions.cart.removeItem(payload);
    mutate('/action/cart/getCart', res);
  }, []);

  const updateItem = useCallback(
    async (lineItemId: string, customLineItemId: string | undefined, newQuantity: number) => {
      try {
        const extensions = sdk.composableCommerce;

        const payload: { lineItem: { id: string; customLineItemId?: string; count: number } } = {
          lineItem: {
            id: lineItemId,
            customLineItemId,
            count: newQuantity,
          },
        };

        const res = await extensions.cart.updateItem(payload);

        if (res.isError) {
          console.error('Error updating cart item:', res.error);
          return { isError: true, error: res.error };
        }

        mutate('/action/cart/getCart', res);

        return { isError: false, data: res.data as Cart };
      } catch (error) {
        console.error('Unexpected error updating cart item:', error);
        return { isError: true, error };
      }
    },
    [],
  );

  const updateCart = useCallback(async (payload: CartDetails): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.updateCart(payload);

    mutate('/action/cart/getCart', res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const setShippingMethod = useCallback(async (shippingMethodId: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      shippingMethod: {
        id: shippingMethodId,
      },
    };

    const res = await extensions.cart.setShippingMethod(payload);

    mutate('/action/cart/getCart', res);
  }, []);

  const redeemDiscountCode = useCallback(async (code: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      code: code,
    };
    const res = await extensions.cart.redeemDiscountCode(payload);

    if (!res.isError && (res.data as Cart).cartId) {
      mutate('/action/cart/getCart', res);
    } else {
      throw new Error((res.isError ? res.error.message : '') ?? 'code not valid');
    }
  }, []);

  const removeDiscountCode = useCallback(async (discount: Discount) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.removeDiscountCode({ discountId: discount.discountId as string });

    mutate('/action/cart/getCart', res);
  }, []);

  const resetCart = useCallback(async () => {
    await sdk.callAction({ actionName: 'cart/resetCart' });
  }, []);

  const getExternalShippingMethods = async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getExternalShippingMethods();

    if (!res.isError && res.data) {
      return res.data;
    }

    throw new Error((res.isError ? res.error.message : '') ?? 'shipping methods not available');
  };

  const setExternalShippingMethod = useCallback(async (shippingCode: string) => {
    const extensions = sdk.composableCommerce;

    const payload = { shippingCode };

    const res = await extensions.cart.setCustomShippingMethod(payload);

    if (!res.isError) {
      await mutate('/action/cart/getCart', res);
    }
    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const setExternalTaxAmount = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.setExternalTaxAmount();

    if (!res.isError) {
      await mutate('/action/cart/getCart', res);
    }
  }, []);

  return {
    ...data,
    totalItems,
    isEmpty,
    isShippingAccurate,
    // Forcing false since we don't track stock
    hasOutOfStockItems: false,
    transaction,
    addItem,
    updateCart,
    setShippingMethod,
    removeItem,
    updateItem,
    estimateShipping,
    orderCart,
    queryOrder,
    getOrder,
    orderHistory,
    getProjectSettings,
    redeemDiscountCode,
    removeDiscountCode,
    resetCart,
    billingAddress,
    shippingAddress,
    getExternalShippingMethods,
    setExternalShippingMethod,
    setExternalTaxAmount,
  };
};

export default useCart;
